/*
  <aub-wall> - a wall of line-drawn laundromat machines that fills its box.

  Centre machine: the Aubels logo turning in the drum, screen shows the drop countdown.
  Machines nearest the centre: one product each, tumbling in the drum, price on the screen,
  the whole machine links to the product. Sold out = "out of order" tape across the door.
  No products yet: line-drawn clothes tumble in the nearest drums instead.

  Attributes
    data-mode       "home" (drums link to products), "lock" (locked page: lifted, nothing links)
                    or "strip" (inner pages: a faint band of machines, no logo drum, no contents)
    data-tape       text for an "out of order" tape across the centre machine (strip mode, e.g. 404)
    data-drums      "logo" puts the spinning logo in every drum (home mode) instead of products/clothes
    data-number     number on the centre machine (default 01)
    data-link       centre drum link (home only)       data-label  its accessible name
    data-drop       drop time, unix seconds; centre screen counts down to it
    data-idle       centre screen text when there is no drop time (default DROP 01)
    data-items      not used; items come from a child <script type="application/json" data-aub-items>
    data-garments   "false" turns off the drawn-clothes fallback
    data-invert     centre machine drawn solid black
    data-still      no motion
*/
(() => {
  if (customElements.get('aub-wall')) return;

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  // Simple line clothes for empty stores and the locked page, drawn in a 100x100 box.
  const GARMENTS = {
    tee: 'M27 33 L41 25 Q50 32 59 25 L73 33 L80 45 L69 50 L66 46 V78 H34 V46 L31 50 L20 45 Z',
    hoodie: 'M31 35 Q50 13 69 35 L80 71 L71 73 L67 53 V81 H33 V53 L29 73 L20 71 Z M40 31 Q50 41 60 31 M46 40 V50 M54 40 V50',
    shorts: 'M29 30 H71 L76 72 H55 L50 48 L45 72 H24 Z M29 36 H71',
    sock: 'M43 18 H59 V58 Q59 66 67 69 Q78 73 74 82 Q70 89 58 85 L41 78 Q33 73 41 63 Z M43 26 H59',
    cap: 'M24 62 Q24 34 50 34 Q76 34 76 62 Z M76 62 H88 Q88 68 76 68 H24 M50 34 V38',
  };
  const GARMENT_ORDER = ['tee', 'hoodie', 'shorts', 'sock', 'cap', 'tee', 'hoodie', 'shorts'];
  const garment = (k) =>
    `<svg viewBox="0 0 100 100" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"><path d="${GARMENTS[k]}" vector-effect="non-scaling-stroke"/></svg>`;

  const machine = ({ label, screen, hero }) => {
    const panel = hero
      ? `<rect x="40" y="9" width="5" height="9" vector-effect="non-scaling-stroke"/>
         <rect class="aub-m__screen" x="50" y="7.5" width="41" height="12" vector-effect="non-scaling-stroke"/>`
      : `<rect x="62" y="9" width="5" height="9" vector-effect="non-scaling-stroke"/>
         <rect class="aub-m__screen" x="72" y="9" width="19" height="9" vector-effect="non-scaling-stroke"/>`;
    const text = screen
      ? `<text class="aub-m__display" x="${hero ? 70.5 : 81.5}" y="${hero ? 15.6 : 15.2}" text-anchor="middle">${esc(screen)}</text>`
      : '';
    return `<svg viewBox="0 0 100 120" fill="none" stroke="currentColor" aria-hidden="true" focusable="false">
      <rect class="aub-m__body" x="2" y="2" width="96" height="116" vector-effect="non-scaling-stroke"/>
      <line x1="2" y1="24" x2="98" y2="24" vector-effect="non-scaling-stroke"/>
      <text class="aub-m__no" x="9" y="16">${esc(label)}</text>
      ${panel}${text}
      <circle cx="50" cy="70" r="36" vector-effect="non-scaling-stroke"/>
      <circle cx="50" cy="70" r="28" vector-effect="non-scaling-stroke"/>
      <rect x="10" y="62" width="4" height="16" vector-effect="non-scaling-stroke"/>
    </svg>`;
  };

  const pad = (n) => String(n).padStart(2, '0');
  const countdown = (target) => {
    const ms = target * 1000 - Date.now();
    if (ms <= 0) return 'OPEN';
    const t = Math.floor(ms / 1000);
    const d = Math.floor(t / 86400);
    const hms = `${pad(Math.floor((t % 86400) / 3600))}:${pad(Math.floor((t % 3600) / 60))}:${pad(t % 60)}`;
    return d > 0 ? `${d}D ${hms}` : hms;
  };

  class AubWall extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('script[data-aub-items]');
      try {
        this.items = data ? JSON.parse(data.textContent) : [];
      } catch (e) {
        this.items = [];
      }
      this.ro = new ResizeObserver(() => this.build());
      this.ro.observe(this);
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      clearInterval(this.timer);
    }

    build() {
      const W = this.clientWidth;
      const H = this.clientHeight;
      if (!W || !H || (W === this._w && H === this._h)) return;
      this._w = W;
      this._h = H;
      clearInterval(this.timer);

      const lock = this.dataset.mode === 'lock';
      const strip = this.dataset.mode === 'strip';
      const target = strip ? (W < 600 ? 120 : 170) : W < 600 ? 150 : 250;
      let cols = Math.max(3, Math.round(W / target));
      if (cols % 2 === 0) cols += 1;
      const mw = W / cols;
      const mh = mw * 1.2;
      let rows = Math.ceil(H / mh) + 1;
      if (rows % 2 === 0) rows += 1;

      this.style.setProperty('--aub-mh', `${mh}px`);
      const cx = W / 2 - mw / 2;
      // strip: put the middle row's door centre (y=70 of 120) on the band's centre line
      const cy = strip ? H / 2 - mh * (70 / 120) : H / 2 - mh / 2 - (lock ? mh * 0.4 : 0);
      const halfR = Math.floor(rows / 2);
      const halfC = Math.floor(cols / 2);
      const s = mw / 100;

      // Every machine, then the visible ones nearest the centre get the products.
      const cells = [];
      let n = 2;
      for (let r = -halfR; r <= halfR; r++) {
        for (let c = -halfC; c <= halfC; c++) {
          const x = cx + c * mw;
          const y = cy + r * mh;
          const hero = r === 0 && c === 0;
          cells.push({ r, c, x, y, hero, label: hero ? this.dataset.number || '01' : pad(n++) });
        }
      }
      // On the locked page the sign-up card covers the bottom of the wall, so keep contents above it.
      const floor = lock ? H - Math.min(300, H * 0.42) : H;
      if (strip) {
        let plain = '';
        for (const cell of cells) {
          plain += `<div class="aub-m${cell.hero ? ' is-centre' : ''}" style="left:${cell.x}px;top:${cell.y}px;width:${mw}px;height:${mh}px">${machine({ label: cell.label, screen: '', hero: false })}</div>`;
        }
        if (this.dataset.tape) {
          const c = cells.find((cell) => cell.hero);
          plain += `<span class="aub-tape" style="left:${c.x + 10 * s}px;top:${c.y + 62 * s}px;width:${80 * s}px;font-size:${Math.max(10, 6.5 * s)}px">${esc(this.dataset.tape)}</span>`;
        }
        this.innerHTML = plain;
        return;
      }
      const visible = (cell) => cell.x >= -1 && cell.x + mw <= W + 1 && cell.y >= -1 && cell.y + mh * 0.85 <= floor;
      const near = cells
        .filter((cell) => !cell.hero && visible(cell))
        .sort((a, b) => Math.hypot(a.c, a.r * 1.2) - Math.hypot(b.c, b.r * 1.2) || a.r - b.r || a.c - b.c);

      const allLogo = this.dataset.drums === 'logo';
      let fill = allLogo ? [] : (this.items || []).map((item) => ({ item }));
      if (!fill.length && !allLogo && this.dataset.garments !== 'false') {
        fill = GARMENT_ORDER.slice(0, W < 600 ? 4 : 6).map((g) => ({ garment: g }));
      }
      fill.slice(0, near.length).forEach((f, i) => Object.assign(near[i], f));

      const drumPos = (cell) => `left:${cell.x + 22 * s}px;top:${cell.y + 42 * s}px;width:${56 * s}px;height:${56 * s}px`;
      const hasDrop = Number(this.dataset.drop) > 0;
      const idle = this.dataset.idle || 'DROP 01';

      let html = '';
      for (const cell of cells) {
        const { item } = cell;
        let screen = '';
        if (cell.hero) screen = hasDrop ? countdown(Number(this.dataset.drop)) : idle;
        else if (item && lock) screen = 'IN USE';
        else if (item) screen = item.s === false ? 'SOLD' : item.p;

        const cls = ['aub-m'];
        if (cell.hero) cls.push('is-hero');
        if (cell.hero && this.dataset.invert != null) cls.push('is-invert');
        if (item || cell.garment) cls.push('is-full');
        html += `<div class="${cls.join(' ')}" style="left:${cell.x}px;top:${cell.y}px;width:${mw}px;height:${mh}px">${machine({ ...cell, screen })}</div>`;

        // Drum contents. A random negative delay puts every drum at a different point in its cycle.
        const delay = `animation-delay:-${(Math.random() * 6).toFixed(2)}s`;
        if (cell.hero) {
          html += `<div class="aub-drum is-hero${lock ? ' is-inuse' : ''}${this.dataset.invert != null ? ' is-invert' : ''}" style="${drumPos(cell)}"><span class="aub-logo" aria-hidden="true"></span></div>`;
          if (!lock && this.dataset.link) {
            html += `<a class="aub-drum-link" href="${esc(this.dataset.link)}" aria-label="${esc(this.dataset.label || 'Shop')}" style="${drumPos(cell)}"></a>`;
          }
        } else if (item) {
          const img = item.i
            ? `<img src="${esc(item.i)}" alt="" loading="lazy" decoding="async" style="${delay}">`
            : `<span class="aub-drum__blank" style="${delay}">${garment('tee')}</span>`;
          html += `<div class="aub-drum is-item" style="${drumPos(cell)}">${img}</div>`;
          if (item.s === false) {
            html += `<span class="aub-tape" style="left:${cell.x + 12 * s}px;top:${cell.y + 64 * s}px;width:${76 * s}px;font-size:${Math.max(9, 6 * s)}px">Out of order</span>`;
          }
          if (!lock && item.u) {
            const name = `${item.t}${item.s === false ? ', sold out' : `, ${item.p}`}`;
            html += `<a class="aub-m-link" href="${esc(item.u)}" aria-label="${esc(name)}" style="left:${cell.x}px;top:${cell.y}px;width:${mw}px;height:${mh}px"></a>`;
          }
        } else if (cell.garment) {
          html += `<div class="aub-drum is-garment" style="${drumPos(cell)}"><span class="aub-drum__blank" style="${delay}">${garment(cell.garment)}</span></div>`;
        } else if (allLogo && visible(cell)) {
          html += `<div class="aub-drum is-logo" style="${drumPos(cell)}"><span class="aub-logo" aria-hidden="true" style="${delay}"></span></div>`;
        }
      }
      this.innerHTML = html;

      if (hasDrop) {
        const screen = this.querySelector('.is-hero .aub-m__display');
        const drop = Number(this.dataset.drop);
        this.timer = setInterval(() => {
          if (screen) screen.textContent = countdown(drop);
        }, 1000);
      }
    }
  }

  customElements.define('aub-wall', AubWall);
})();
